import http from '../../../utils/andromeda-api';
import store from '../../../redux/store';
import { selectedOrganizationSelector } from '../../../redux/selectors/organization';
import { userDetailSelector } from '../../../redux/selectors/user';

const getOrganizationId = () => {
  return selectedOrganizationSelector(store.getState()).id;
};

const getUserId = () => {
  return userDetailSelector(store.getState()).id;
};

export const getRows = async () => {
  try {
    const organizationId = getOrganizationId();
    const {
      data: { result },
    } = await http.get(`/savedRows/${organizationId}`);
    return result;
  } catch (error) {
    console.error('Error in getRows:', error);
    throw error;
  }
};

export const getFolders = async ({ isSA, isSU, canPublish } = {}) => {
  try {
    const organizationId = getOrganizationId();
    // Superuser and above (isSU cascades up to Super Admin / Super Duper Admin).
    const isSuperuser = Boolean(isSU);
    const {
      data: { result },
    } = await http.get(`/savedRows/folders/${organizationId}`);

    const isNARFolder = name => name.includes('NAR');

    const getFolderGroup = folder => {
      if (folder.isUniversal && isNARFolder(folder.name)) return 2; // NAR — always bottom
      if (folder.isUniversal) return 0; // General universal — top
      return 1; // Customer-created — middle, sorted by createdAt desc
    };

    const sortedResult = [...result].sort((a, b) => {
      const groupDiff = getFolderGroup(a) - getFolderGroup(b);
      if (groupDiff !== 0) return groupDiff;
      // sort most recent first within each group
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    return {
      emptyRows: true,
      defaultRows: true,
      selectedRowType: 'defaultRows',
      externalContentURLs: sortedResult.map(folder => {
        let behaviors = { canEdit: true, canDelete: true };
        if (folder.isUniversal && !isSA) {
          behaviors = { canEdit: false, canDelete: false };
        }
        // Publisher Folders publish to all customers, so editing requires the
        // CAN_PUBLISH privilege within the owning org; superusers may edit any.
        // Everyone without CAN_PUBLISH (Staff Support, In House Testing) gets
        // read/use only (drag-and-drop).
        if (
          folder.isPublisherFolder &&
          !isSuperuser &&
          !(canPublish && folder.organizationId === organizationId)
        ) {
          behaviors = { canEdit: false, canDelete: false };
        }

        return {
          // Glyphs (⁂ universal, 🌐︎ publisher) are baked into the DB name at
          // creation, so the raw name already carries the panel marker.
          name: folder.name,
          value: folder.id,
          handle: folder.id,
          isLocal: true,
          behaviors,
          isUniversal: folder.isUniversal,
          isPublisherFolder: folder.isPublisherFolder,
        };
      }),
    };
  } catch (error) {
    console.error('Error in getFolders:', error);
    throw error;
  }
};

export const saveRow = async ({ name, content, folderId, id }) => {
  try {
    if (!name || !content || !folderId)
      throw new Error('name, content and folderId are required fields');
    const organizationId = getOrganizationId();

    const { data } = await http.post('/savedRows', {
      organizationId,
      name,
      content,
      folderId,
      id,
    });

    return data;
  } catch (error) {
    console.error('Error in saveRow:', error);
    throw error;
  }
};

export const saveFolder = async ({
  name,
  id,
  isUniversal,
  isPublisherFolder,
}) => {
  try {
    if (!name || !id) throw new Error('name and id are required fields');
    const organizationId = getOrganizationId();

    const { data } = await http.post('/savedRows/folder', {
      organizationId,
      name,
      id,
      isUniversal,
      isPublisherFolder,
    });

    return data;
  } catch (error) {
    console.error('Error in saveFolder:', error);
    throw error;
  }
};

export const deleteFolder = async id => {
  try {
    if (!id) throw new Error('id is required field');

    const { data } = await http.delete(`/savedRows/folder/${id}`);

    return data;
  } catch (error) {
    console.error('Error in deleteFolder:', error);
    throw error;
  }
};

export const editRow = async ({ rowId, content, name, folderId }) => {
  try {
    if (!rowId) throw new Error('rowId is required');
    const { data } = await http.put(`/savedRows/${rowId}`, {
      content,
      name,
      folderId,
    });

    return data;
  } catch (error) {
    console.error('Error in editRow:', error);
    throw error;
  }
};

export const deleteRow = async rowId => {
  try {
    if (!rowId) throw new Error('rowId is required');

    const { data } = await http.delete(`/savedRows/${rowId}`);

    return data;
  } catch (error) {
    console.error('Error in deleteRow:', error);
    throw error;
  }
};

export const getSortOrder = async () => {
  try {
    const organizationId = getOrganizationId();
    const userId = getUserId();
    const { data } = await http.get(
      `/savedRows/sortOrder/${organizationId}/${userId}`
    );
    return data.result.sortOrder;
  } catch (error) {
    console.error('Error in getSortOrder:', error);
    throw error;
  }
};

export const updateSortOrder = async sortOrder => {
  try {
    const organizationId = getOrganizationId();
    const userId = getUserId();
    const { data } = await http.patch(
      `/savedRows/sortOrder/${organizationId}/${userId}`,
      { sortOrder }
    );
    return data;
  } catch (error) {
    console.error('Error in updateSortOrder:', error);
    throw error;
  }
};
